import React from 'react';
import { ArrowLeft, Book } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { useLanguageStore } from '../../lib/languageStore';
import { translate } from '../../lib/translations';

interface RuleSection {
  title: string;
  rules: Array<{
    id: string;
    text: string;
    subrules?: Array<{ id: string; text: string }>;
  }>;
}

export function Rules() {
  const navigate = useNavigate();
  const { language } = useLanguageStore();

  const rules: RuleSection[] = [
    {
      title: '1. Configuration de la Ligue',
      rules: [
        { 
          id: '1.1', 
          text: 'Type de ligue et accessibilité',
          subrules: [
            { id: '1.1.1', text: 'Ligue publique : Tout le monde peut envoyer une demande d\'adhésion' },
            { id: '1.1.2', text: 'Ligue privée : Seuls les membres invités peuvent rejoindre directement' }
          ]
        },
        { 
          id: '1.2', 
          text: 'Paramètres configurables par le commissaire :',
          subrules: [
            { id: '1.2.1', text: 'Plafond salarial principal et optionnel pour les réserves' },    
            { id: '1.2.2', text: 'Composition d\'équipe (nombre d\'attaquants, défenseurs, gardiens, réserves)' },
            { id: '1.2.3', text: 'Système de pointage personnalisé par position' },
            { id: '1.2.4', text: 'Limites de protection en fin de saison par position' },
            { id: '1.2.5', text: 'Limites de rétrogradations et signatures par saison' },
            { id: '1.2.6', text: 'Système de blessures (limite de blessés et durée de retour)' },
            { id: '1.2.7', text: 'Paramètres de repêchage (mode linéaire/serpentin, temps par choix)' }
          ]
        }
      ]
    },
    {
      title: '2. Système de Pointage',
      rules: [
        {
          id: '2.1',
          text: 'Points configurables pour les Attaquants :',
          subrules: [
            { id: '2.1.1', text: 'But : Points définis par le commissaire (défaut: 1 point)' },
            { id: '2.1.2', text: 'Passe : Points définis par le commissaire (défaut: 1 point)' }
          ]
        },
        {
          id: '2.2',
          text: 'Points configurables pour les Défenseurs :',
          subrules: [
            { id: '2.2.1', text: 'But : Points définis par le commissaire (défaut: 2 points)' },
            { id: '2.2.2', text: 'Passe : Points définis par le commissaire (défaut: 1 point)' }
          ]
        },
        {
          id: '2.3',
          text: 'Points configurables pour les Gardiens :',
          subrules: [
            { id: '2.3.1', text: 'Victoire : Points définis par le commissaire (défaut: 2 points)' },
            { id: '2.3.2', text: 'Blanchissage : Points définis par le commissaire (défaut: 3 points)' },
            { id: '2.3.3', text: 'But : Points définis par le commissaire (défaut: 5 points)' }
          ]
        },
        {
          id: '2.4',
          text: 'Les points sont automatiquement calculés et intégrés au classement en temps réel'
        }
      ]
    },
    {
      title: '3. Composition et Gestion d\'Équipe',
      rules: [
        { 
          id: '3.1', 
          text: 'Structure d\'équipe définie par le commissaire :',
          subrules: [
            { id: '3.1.1', text: 'Club Principal (NHL) : Nombres fixes d\'attaquants, défenseurs et gardiens' },
            { id: '3.1.2', text: 'Équipe Réserve : Nombre maximum de joueurs toutes positions confondues' }
          ]
        },
        {
          id: '3.2',
          text: 'Gestion des mouvements de joueurs :',
          subrules: [
            { id: '3.2.1', text: 'Promotion : Faire monter un joueur des réserves au club principal' },
            { id: '3.2.2', text: 'Rétrogradation : Faire descendre un joueur du club principal aux réserves' },
            { id: '3.2.3', text: 'Échange entre joueurs : Permuter directement un joueur principal et un réserviste' },
            { id: '3.2.4', text: 'Libération : Retirer complètement un joueur de l\'équipe' }
          ]
        },
        {
          id: '3.3',
          text: 'Limites saisonnières configurables :',
          subrules: [
            { id: '3.3.1', text: 'Nombre maximum de rétrogradations par saison par équipe' },
            { id: '3.3.2', text: 'Nombre maximum de signatures d\'agents libres par saison par équipe' }
          ]
        }
      ]
    },
    {
      title: '4. Plafond Salarial',
      rules: [
        {
          id: '4.1',
          text: 'Application du plafond salarial (configurable) :',
          subrules: [
            { id: '4.1.1', text: 'Club Principal : Plafond salarial obligatoire si activé' },
            { id: '4.1.2', text: 'Équipe Réserve : Plafond salarial optionnel et séparé si activé' }
          ]
        },
        {
          id: '4.2',
          text: 'Calcul automatique du plafond salarial en temps réel pour tous les mouvements'
        },
        {
          id: '4.3',
          text: 'Les salaires sont basés sur les contrats NHL officiels avec années restantes'
        }
      ]
    },
    {
      title: '5. Agents Libres',
      rules: [
        {
          id: '5.1',
          text: 'Signature d\'agents libres soumise aux contraintes suivantes :',
          subrules: [
            { id: '5.1.1', text: 'Respect des limites d\'effectif par position' },
            { id: '5.1.2', text: 'Respect du plafond salarial applicable (principal ou réserve)' },
            { id: '5.1.3', text: 'Limite saisonnière de signatures si configurée' }
          ]
        },
        {
          id: '5.2',
          text: 'Assignation lors de la signature :',
          subrules: [
            { id: '5.2.1', text: 'Club Principal (NHL) : Joueur actif comptant dans l\'effectif principal' },
            { id: '5.2.2', text: 'Équipe Réserve : Joueur de réserve comptant dans l\'effectif réserve' }
          ]
        },
        {
          id: '5.3',
          text: 'Interface séparée pour joueurs et gardiens agents libres avec filtres avancés'
        }
      ]
    },
    {
      title: '6. Centre d\'Échanges',
      rules: [
        {
          id: '6.1',
          text: 'Processus d\'échange automatisé :',
          subrules: [
            { id: '6.1.1', text: 'Sélection d\'équipe adverse et négociation de joueurs/gardiens' },
            { id: '6.1.2', text: 'Validation automatique des contraintes d\'effectif et salariales' },
            { id: '6.1.3', text: 'Envoi de proposition via le système de messages' },
            { id: '6.1.4', text: 'Acceptation/refus par l\'équipe receveuse' }
          ]
        },
        {
          id: '6.2',
          text: 'Validations automatiques lors des échanges :',
          subrules: [
            { id: '6.2.1', text: 'Limites d\'effectif pour chaque équipe impliquée' },
            { id: '6.2.2', text: 'Plafonds salariaux (principal et réserve) pour chaque équipe' },
            { id: '6.2.3', text: 'Messages d\'erreur détaillés en cas de non-conformité' }
          ]
        }
      ]
    },
    {
      title: '7. Liste de Protection',
      rules: [
        {
          id: '7.1',
          text: 'Configuration des protections (définie par le commissaire) :',
          subrules: [
            { id: '7.1.1', text: 'Nombre d\'attaquants à protéger par équipe' },
            { id: '7.1.2', text: 'Nombre de défenseurs à protéger par équipe' },
            { id: '7.1.3', text: 'Nombre de gardiens à protéger par équipe' },
            { id: '7.1.4', text: 'Nombre de réservistes à protéger par équipe (optionnel)' }
          ]
        },
        {
          id: '7.2',
          text: 'Gestion de la liste de protection :',
          subrules: [
            { id: '7.2.1', text: 'Interface dédiée pour sélectionner les joueurs à protéger' },
            { id: '7.2.2', text: 'Validation automatique du respect des limites par position' },
            { id: '7.2.3', text: 'Distinction automatique entre joueurs NHL et réservistes' }
          ]
        }
      ]
    },
    {
      title: '8. Système de Messages',
      rules: [
        {
          id: '8.1',
          text: 'Centre de messages intégré incluant :',
          subrules: [
            { id: '8.1.1', text: 'Offres d\'échange reçues et envoyées' },
            { id: '8.1.2', text: 'Messages de la ligue du commissaire' },
            { id: '8.1.3', text: 'Demandes d\'adhésion à approuver (commissaire seulement)' }
          ]
        },
        {
          id: '8.2',
          text: 'Traitement automatique des réponses aux échanges et demandes d\'adhésion'
        }
      ]
    },
    {
      title: '9. Système de Blessures',
      rules: [
        {
          id: '9.1',
          text: 'Gestion des blessures (configurable) :',
          subrules: [
            { id: '9.1.1', text: 'Limite maximum de joueurs blessés par équipe' },
            { id: '9.1.2', text: 'Durée configurable pour le retour de blessure' }
          ]
        }
      ]
    },
    {
      title: '10. Système de Repêchage',
      rules: [
        {
          id: '10.1',
          text: 'Configuration du repêchage (définie par le commissaire) :',
          subrules: [
            { id: '10.1.1', text: 'Mode linéaire : Ordre de sélection fixe selon le classement pour chaque tour' },
            { id: '10.1.2', text: 'Mode serpentin : Direction alternée chaque tour (1er→dernier, puis dernier→1er)' },
            { id: '10.1.3', text: 'Temps par choix configurable (en minutes) avec décompte automatique' },
            { id: '10.1.4', text: 'Activation/désactivation du mode repêchage par le commissaire' }
          ]
        },
        {
          id: '10.2',
          text: 'Liste de priorité personnalisée :',
          subrules: [
            { id: '10.2.1', text: 'Création d\'une liste de joueurs prioritaires pour chaque équipe' },
            { id: '10.2.2', text: 'Réorganisation par glisser-déposer ou flèches directionnelles' },
            { id: '10.2.3', text: 'Assignation automatique NHL/Réserve pour chaque joueur de la liste' },
            { id: '10.2.4', text: 'Sélection automatique du premier joueur disponible si temps écoulé' }
          ]
        },
        {
          id: '10.3',
          text: 'Interface de repêchage en temps réel :',
          subrules: [
            { id: '10.3.1', text: 'Affichage de l\'ordre de repêchage avec équipe active en surbrillance' },
            { id: '10.3.2', text: 'Décompte visuel du temps restant pour le choix actuel' },
            { id: '10.3.3', text: 'Historique des dernières sélections effectuées' },
            { id: '10.3.4', text: 'Calcul automatique des tours restants avant votre prochain choix' }
          ]
        },
        {
          id: '10.4',
          text: 'Sélection automatisée :',
          subrules: [
            { id: '10.4.1', text: 'Sélection automatique en cas de dépassement du temps imparti' },
            { id: '10.4.2', text: 'Respect de la liste de priorité personnalisée de l\'équipe' },
            { id: '10.4.3', text: 'Validation automatique des contraintes d\'effectif et salariales' },
            { id: '10.4.4', text: 'Passage automatique au joueur suivant après chaque sélection' }
          ]
        },
        {
          id: '10.5',
          text: 'Gestion des tours consécutifs (mode serpentin) :',
          subrules: [
            { id: '10.5.1', text: 'Réinitialisation automatique du temps pour les choix consécutifs' },
            { id: '10.5.2', text: 'Maintien de l\'équipe active pour les tours de transition' },
            { id: '10.5.3', text: 'Calcul automatique de la direction selon le numéro de tour' }
          ]
        },
        {
          id: '10.6',
          text: 'Restrictions pendant le repêchage :',
          subrules: [
            { id: '10.6.1', text: 'Signature d\'agents libres autorisée uniquement pendant votre tour' },
            { id: '10.6.2', text: 'Blocage des échanges pendant la période de repêchage active' },
            { id: '10.6.3', text: 'Respect des limites d\'effectif et plafonds salariaux lors des sélections' }
          ]
        }
      ]
    },
    {
      title: '11. Joueurs Scratch',
      rules: [
        {
          id: '11.1',
          text: 'Définition et gestion des joueurs scratch :',
          subrules: [
            { id: '11.1.1', text: 'Un joueur scratch est un joueur actif dans le club principal qui ne figure pas sur la feuille de match de son équipe réelle (healthy scratch).' },
            { id: '11.1.2', text: 'Dans Dynasty Pool, un joueur scratch est déplacé dans la section "Scratch" de votre équipe et ne rapporte aucun point tant qu’il reste scratch.' },
            { id: '11.1.3', text: 'Seuls les joueurs du club principal peuvent être scratchés.' },
            { id: '11.1.4', text: 'Un joueur scratch conserve son contrat et son salaire dans le cap salarial du club principal.' },
            { id: '11.1.5', text: 'Le nombre de joueurs scratch autorisés est configurable dans les paramètres de la ligue.' },
            { id: '11.1.6', text: 'Les joueurs scratch ne comptent pas dans les limites d’effectif principal ou réserve.' },
            { id: '11.1.7', text: 'Un joueur peut être déplacé vers scratch via le menu de gestion d’équipe ou lors de sa signature comme agent libre.' },
            { id: '11.1.8', text: 'Pour réactiver un joueur scratch, il doit être retiré du statut scratch via la gestion d’équipe.' }
          ]
        },            
      ]
    }
  ];

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={() => goBackOrHome(navigate)}
        >
          <ArrowLeft size={20} className="me-2" />
          {translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <Book className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-4">
            {translate('league.rules', language)}
          </h2>
        </div>

        <div className="accordion" id="rulesAccordion">
          {rules.map((section, index) => (
            <div className="accordion-item border-0 mb-3" key={section.title}>
              <h3 className="accordion-header">
                <button
                  className={`accordion-button ${index !== 0 ? 'collapsed' : ''}`}
                  type="button"
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapse${index}`}
                  aria-expanded={index === 0}
                  aria-controls={`collapse${index}`}
                >
                  {section.title}
                </button>
              </h3>
              <div
                id={`collapse${index}`}
                className={`accordion-collapse collapse ${index === 0 ? 'show' : ''}`}
                data-bs-parent="#rulesAccordion"
              >
                <div className="accordion-body">
                  <ol className="list-group list-group-flush list-group-numbered">
                    {section.rules.map((rule) => (
                      <li key={rule.id} className="list-group-item bg-transparent">
                        {rule.text}
                        {rule.subrules && (
                          <ul className="list-unstyled mt-2 ms-3">
                            {rule.subrules.map((subrule) => (
                              <li key={subrule.id} className="mt-1">
                                • {subrule.text}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}